import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtGHS, pName } from "@/lib/format";
import { Plus, Search, Pencil, Pill, Upload, FileDown, ScanLine, Camera, Trash2, Download, Copy, History, Power } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { ScanBox } from "@/components/ScanBox";
import { PriceUpdateDialog, isPriceFile } from "@/components/PriceUpdateDialog";
import { DuplicatesDialog, DuplicateWarning } from "@/components/DuplicatesDialog";
import { ImportHistoryDialog } from "@/components/ImportHistoryDialog";
import { CameraScanner } from "@/components/CameraScanner";
import { toast } from "sonner";

const SCHEDULES = ["OTC", "POM", "CONTROLLED"];
const FORMS = ["Tablet", "Capsule", "Syrup", "Suspension", "Bottle", "Suppository", "Vial", "Ampoule", "Injection", "Cream", "Ointment", "Drops", "Inhaler", "Sachet", "Unit"];
const empty = {
  generic_name: "", brand: "", form: "", strength: "",
  pack_size: "", units_per_pack: "", unit_of_measure: "", category_id: "", supplier_id: "", drug_schedule: "OTC", barcode: "", reorder_level: 10,
  cost_price: 0, is_active: true,
};

export default function Products() {
  const { can, isSuperAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [q, setQ] = useState("");
  const [schedule, setSchedule] = useState("all");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [barcodeCamOpen, setBarcodeCamOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/products", { params: { q: q || undefined, schedule: schedule === "all" ? undefined : schedule, category_id: category === "all" ? undefined : category } })
      .then((r) => setProducts(r.data)).catch(() => {});
  }, [q, schedule, category]);

  useEffect(() => { load(); }, [load]);
  const shown = status === "all" ? products : products.filter((p) => (p.is_active !== false) === (status === "active"));
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const toggleActive = async (p) => {
    const next = p.is_active === false;
    try {
      await api.patch(`/products/${p.id}/active`, { is_active: next });
      toast.success(`${pName(p)} ${next ? "reactivated" : "deactivated"}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setEditId(p.id); setOpen(true); };

  const confirmDelete = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/products/${delTarget.id}`);
      toast.success(`${pName(delTarget)} deleted`);
      setDelTarget(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const handleScan = async (code) => {
    try {
      const { data } = await api.get("/products", { params: { barcode: code } });
      const p = (data || [])[0];
      if (!p) { toast.error(`No product matches barcode ${code}`); return; }
      setQ(p.sku);
      toast.success(`Found ${pName(p)}`);
      if (can("products.manage")) openEdit(p);
    } catch (e) { toast.error(apiError(e)); }
  };

  const [exporting, setExporting] = useState(false);
  const [pricePreview, setPricePreview] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/products/export", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = `products_${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); } finally { setExporting(false); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const text = await file.text();
      if (isPriceFile(text)) {
        const { data } = await api.post("/products/import-prices", { csv_text: text, dry_run: true });
        setPricePreview({ data, text, file: file.name });
        return;
      }
      const { data } = await api.post("/products/import", { csv_text: text });
      toast.success(`Imported ${data.created} new product(s)`, { duration: 8000 });
      if (data.skipped_duplicates) toast.warning(`${data.skipped_duplicates} row(s) skipped — these products already exist`, { duration: 10000 });
      if (data.errors?.length) toast.error(`${data.errors.length} row(s) skipped`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { e.target.value = ""; }
  };
  const downloadTemplate = () => {
    const csv = "generic_name,brand,form,strength,pack_size,units_per_pack,category,supplier,drug_schedule,cost_price,reorder_level\nParacetamol,Panadol,Tablet,500mg,Box of 10 strips,10,Analgesics,Kinapharma Ltd,OTC,0.15,200\n";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "products_template.csv"; a.click(); URL.revokeObjectURL(url);
  };

  const [dupMatches, setDupMatches] = useState(null);
  useEffect(() => { setDupMatches(null); }, [form.generic_name, form.strength, form.form, form.brand, open]);

  const save = async (force = false) => {
    const upp = String(form.units_per_pack ?? "").trim();
    if (upp && !(Number.isInteger(Number(upp)) && Number(upp) >= 1)) { toast.error("Units per pack must be a whole number of 1 or more, or left empty"); return; }
    setSaving(true);
    try {
      if (!force) {
        const { data } = await api.post("/products/duplicate-check", {
          generic_name: form.generic_name || "", strength: form.strength || "", form: form.form || "", brand: form.brand || "", exclude_id: editId || "",
        });
        if (data.matches.length) { setDupMatches(data.matches); return; }
      }
      const payload = {
        generic_name: form.generic_name, brand: form.brand,
        form: form.form, strength: form.strength, pack_size: form.pack_size, unit_of_measure: form.unit_of_measure || "",
        units_per_pack: String(form.units_per_pack ?? "").trim() ? Number(form.units_per_pack) : null,
        category_id: form.category_id || null, supplier_id: form.supplier_id || null, drug_schedule: form.drug_schedule,
        barcode: form.barcode, reorder_level: Number(form.reorder_level),
        cost_price: Number(form.cost_price), is_active: form.is_active,
      };
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success(editId ? "Product updated" : "Product created");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  return (
    <div>
      <PageHeader
        title="Product Catalog"
        subtitle="Master list of medicines. SKUs are auto-generated; selling prices derive from cost + markups"
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" data-testid="product-export-btn" disabled={exporting} onClick={exportCsv}><Download className="h-4 w-4 mr-1.5" /> {exporting ? "Preparing…" : "Export CSV"}</Button>
            {isSuperAdmin && <Button variant="outline" data-testid="find-duplicates-btn" onClick={() => setDupOpen(true)}><Copy className="h-4 w-4 mr-1.5" /> Find duplicates</Button>}
            {isSuperAdmin && <Button variant="outline" data-testid="import-history-btn" onClick={() => setHistOpen(true)}><History className="h-4 w-4 mr-1.5" /> Import history</Button>}
            {can("products.manage") && <>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" data-testid="product-csv-input" onChange={importCsv} />
            <Button variant="outline" data-testid="product-template-btn" onClick={downloadTemplate}><FileDown className="h-4 w-4 mr-1.5" /> Template</Button>
            <Button variant="outline" data-testid="product-import-btn" onClick={() => fileRef.current?.click()}><Upload className="h-4 w-4 mr-1.5" /> Import CSV</Button>
            <Button data-testid="add-product-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1.5" /> Add Product</Button>
            </>}
          </div>
        )}
      />

      <ImportHistoryDialog open={histOpen} onOpenChange={setHistOpen} onChanged={load} />
      <DuplicatesDialog open={dupOpen} onOpenChange={setDupOpen} onChanged={load} />
      <PriceUpdateDialog preview={pricePreview} onClose={() => setPricePreview(null)} onApplied={() => { setPricePreview(null); load(); }} />

      <Card className="p-3 mb-4 shadow-sm flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
          <Input data-testid="product-search-input" placeholder="Search name, brand, SKU…" value={q}
            onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <Select value={schedule} onValueChange={setSchedule}>
          <SelectTrigger data-testid="product-schedule-filter" className="w-full sm:w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All schedules</SelectItem>
            {SCHEDULES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="product-category-filter" className="w-full sm:w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {[...cats].sort((a, b) => a.name.localeCompare(b.name)).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="product-status-filter" className="w-full sm:w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <ScanBox onScan={handleScan} placeholder="Scan to find a product…" className="w-full sm:w-72" />
      </Card>

      <Card className="shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground">
              <tr className="text-left">
                <th className="py-2.5 px-3">SKU</th><th className="py-2.5 px-3">Medicine</th>
                <th className="py-2.5 px-3">Schedule</th><th className="py-2.5 px-3">Category</th><th className="py-2.5 px-3">Supplier</th>
                <th className="py-2.5 px-3 text-right">Cost</th><th className="py-2.5 px-3 text-right">Cash</th>
                <th className="py-2.5 px-3 text-right">Corp.</th>
                <th className="py-2.5 px-3 text-right">Reorder</th><th className="py-2.5 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={10} className="py-10 text-center text-muted-foreground">
                  <Pill className="h-8 w-8 mx-auto mb-2 opacity-40" />No products found</td></tr>
              )}
              {shown.map((p) => (
                <tr key={p.id} className={`border-t border-border hover:bg-muted/50 ${p.is_active === false ? "opacity-60" : ""}`}>
                  <td className="py-2 px-3 font-mono text-xs">{p.sku}</td>
                  <td className="py-2 px-3">
                    <div className="font-medium text-foreground">{pName(p)}{p.is_active === false && <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground" data-testid={`inactive-badge-${p.sku}`}>Inactive</span>}</div>
                    <div className="text-xs text-muted-foreground">{p.brand ? "" : "No brand"}{!(p.form || "").trim() && <span className="text-amber-600" data-testid={`missing-form-${p.sku}`}> · no dosage form</span>}</div>
                  </td>
                  <td className="py-2 px-3"><StatusBadge value={p.drug_schedule} /></td>
                  <td className="py-2 px-3 text-muted-foreground">{p.category_name || "—"}</td>
                  <td className="py-2 px-3 text-muted-foreground">{p.supplier_name || "—"}</td>
                  <td className="py-2 px-3 text-right font-mono text-xs">{fmtGHS(p.cost_price)}</td>
                  <td className="py-2 px-3 text-right font-mono text-xs">{fmtGHS(p.price_cash)}</td>
                  <td className="py-2 px-3 text-right font-mono text-xs">{fmtGHS(p.price_corporate)}</td>
                  <td className="py-2 px-3 text-right font-mono text-xs">{p.reorder_level}</td>
                  <td className="py-2 px-3 text-right">
                    {can("products.manage") && (
                      <Button variant="ghost" size="sm" data-testid={`edit-product-${p.sku}`} onClick={() => openEdit(p)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {can("products.manage") && (
                      <Button variant="ghost" size="sm" title={p.is_active === false ? "Reactivate" : "Deactivate"} data-testid={`toggle-active-${p.sku}`} onClick={() => toggleActive(p)}
                        className={p.is_active === false ? "text-emerald-600" : "text-muted-foreground"}>
                        <Power className="h-3.5 w-3.5" /><span className="ml-1 text-xs">{p.is_active === false ? "Reactivate" : "Deactivate"}</span>
                      </Button>
                    )}
                    {isSuperAdmin && (
                      <Button variant="ghost" size="sm" className="text-rose-600 hover:text-rose-700" data-testid={`delete-product-${p.sku}`} onClick={() => setDelTarget(p)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>SKU</Label><Input data-testid="product-sku-input" value={editId ? form.sku : "Auto-generated"} disabled className="bg-muted" /></div>
            <div><Label>Barcode</Label>
              <div className="relative mt-1.5">
                <ScanLine className="h-4 w-4 absolute left-3 top-2.5 text-emerald-500" />
                <Input data-testid="product-barcode-input" value={form.barcode} onChange={set("barcode")}
                  placeholder="Scan or type the pack barcode" className="pl-9 pr-10"
                  onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }} />
                <button type="button" onClick={() => setBarcodeCamOpen(true)} title="Scan with phone camera"
                  data-testid="product-barcode-camera-btn" className="absolute right-2.5 top-2.5 text-emerald-600 hover:text-emerald-700">
                  <Camera className="h-4 w-4" />
                </button>
              </div>
              <CameraScanner open={barcodeCamOpen} onOpenChange={setBarcodeCamOpen} onDetected={(code) => set("barcode")(code)} />
            </div>
            <div className="col-span-2"><Label>Generic name</Label><Input data-testid="product-name-input" value={form.generic_name} onChange={set("generic_name")} /></div>
            <div><Label>Brand</Label><Input data-testid="product-brand-input" value={form.brand} onChange={set("brand")} /></div>
            <div><Label>Form</Label>
              <Select value={form.form || ""} onValueChange={set("form")}>
                <SelectTrigger data-testid="product-form-select"><SelectValue placeholder="Select form" /></SelectTrigger>
                <SelectContent>{FORMS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Strength</Label><Input data-testid="product-strength-input" placeholder="500mg" value={form.strength} onChange={set("strength")} /></div>
            <div><Label>Pack size</Label><Input value={form.pack_size} onChange={set("pack_size")} /></div>
            <div><Label>Counting unit <span className="text-xs text-muted-foreground">(optional)</span></Label><Input placeholder="e.g. Tablet, Strip, Pack" data-testid="product-uom-input" value={form.unit_of_measure || ""} onChange={set("unit_of_measure")} /></div>
            <div><Label>Units per pack <span className="text-xs text-muted-foreground">(optional)</span></Label>
              <Input type="number" min={1} step={1} placeholder="e.g. 10" data-testid="product-upp-input" value={form.units_per_pack ?? ""} onChange={set("units_per_pack")} />
              <p className="text-[11px] text-muted-foreground mt-1">Selling units in one supplier pack. Lets staff receive and order by the pack.</p></div>
            <div className="flex items-center gap-3 rounded-md border px-3 py-2">
              <Switch checked={form.is_active !== false} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="product-active-switch" />
              <div><div className="text-sm font-medium">{form.is_active !== false ? "Active" : "Inactive"}</div>
                <div className="text-[11px] text-muted-foreground">Inactive products are hidden from POS and pickers; stock and history stay.</div></div>
            </div>
            <div>
              <Label>Schedule</Label>
              <Select value={form.drug_schedule} onValueChange={set("drug_schedule")}>
                <SelectTrigger data-testid="product-schedule-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SCHEDULES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Category</Label>
              <Select value={form.category_id || "none"} onValueChange={(v) => set("category_id")(v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Uncategorised</SelectItem>
                  {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Preferred supplier</Label>
              <Select value={form.supplier_id || "none"} onValueChange={(v) => set("supplier_id")(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="product-supplier-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No preferred supplier</SelectItem>
                  {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1.5">Auto-generated purchase orders route this product to its preferred supplier.</p>
            </div>
            <div><Label>Cost price (GHS)</Label><Input type="number" step="0.01" data-testid="product-cost-input" value={form.cost_price} onChange={set("cost_price")} /></div>
            <div><Label>Reorder level</Label><Input type="number" value={form.reorder_level} onChange={set("reorder_level")} /></div>
            <div className="col-span-2 rounded-lg p-3 text-xs" style={{ background: "var(--mm-surface-raised)", color: "var(--mm-text-2)" }}>
              Selling prices are calculated automatically from cost price × approved markups. Set cash &amp; corporate markups in <span className="font-semibold">Business rules</span>, and per-company markups under <span className="font-semibold">Customers → Insurance schemes</span>.
            </div>
          </div>
          {dupMatches && <DuplicateWarning matches={dupMatches} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            {dupMatches
              ? <Button data-testid="save-anyway-btn" variant="destructive" onClick={() => save(true)} disabled={saving}>{saving ? "Saving…" : "Save anyway"}</Button>
              : <Button data-testid="save-product-btn" onClick={() => save()} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete product?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            This permanently deletes <span className="font-medium text-foreground">{delTarget?.generic_name}</span> ({delTarget?.sku}).
            Products with any transaction history can't be deleted — disable them instead.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-delete-product-btn" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
